import { InfoTooltip as InfoIcon } from "./InfoTooltip";

interface DistributionRow {
  ex_div_date: string;
  record_date: string;
  payable_date: string;
  nav: string;
  lt_cap_gain: string;
  st_cap_gain: string;
  dividend_income: string;
  return_of_capital: string;
  total_distribution: string;
}

const COLUMN_TIPS = {
  ex_div_date:
    "The first date on which a buyer of the fund's shares is not entitled to receive the upcoming distribution. Shares must be purchased before this date to be eligible.",
  record_date:
    "The date on which a shareholder must be on the fund's books to receive the distribution.",
  payable_date: "The date on which the distribution is paid to eligible shareholders.",
  nav: "The fund's net asset value per share on the date the distribution was declared.",
  lt_cap_gain:
    "Long-term capital gains: profits from selling investments held for more than one year, passed on to shareholders.",
  st_cap_gain:
    "Short-term capital gains: profits from selling investments held for one year or less, passed on to shareholders.",
  dividend_income:
    "Income from the interest and dividends earned by the fund's holdings and paid out to shareholders.",
  return_of_capital:
    "A return of capital is a distribution that exceeds the fund's earnings and profits. It is not taxed as income but instead reduces your cost basis in the fund's shares.",
  total_distribution:
    "The total amount paid per share on this date, combining income and any capital gains.",
};

export function FundDistributionHistory({
  rows,
  fundName,
  distributionFrequency,
}: {
  rows: DistributionRow[] | null | undefined;
  fundName?: string;
  distributionFrequency?: string | null;
}) {
  const hasRows = rows && rows.length > 0;
  const strippedName = fundName?.replace(/^[Tt]he\s+/, "") ?? null;
  const subject = strippedName ? `the ${strippedName}` : "the Fund";
  const cadence =
    distributionFrequency && distributionFrequency !== "None"
      ? distributionFrequency.toLowerCase()
      : null;

  return (
    <section className="border-b border-[var(--etf-border)]" id="sec-distributions">
      <div className="flex items-center justify-between px-10 max-md:px-4 py-6 max-md:py-4 border-b border-[var(--etf-border)]">
        <h2 className="font-[family-name:var(--font-f37bolton)] text-[24px] max-[480px]:text-[20px] text-[var(--etf-white)] tracking-[-0.02em] leading-none flex items-center gap-3">
          <span className="inline-block w-[3px] h-5 bg-[var(--etf-orange)]" />
          Distributions
        </h2>
        <span className="font-[family-name:var(--font-geist)] text-[12px] tracking-[0.08em] text-[var(--etf-white3)]">
          Dividend &amp; capital gains history · Monthly cadence
        </span>
      </div>

      <div className="px-0 py-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[760px]">
            <thead>
              <tr className="border-b border-[var(--etf-border2)]">
                <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white2)] font-normal text-left px-10 max-md:px-4 py-3.5 whitespace-nowrap">
                  Ex-Dividend Date
                  <InfoIcon tip={COLUMN_TIPS.ex_div_date} direction="right" />
                </th>
                <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white2)] font-normal text-left px-4 py-3.5 whitespace-nowrap">
                  Record Date
                  <InfoIcon tip={COLUMN_TIPS.record_date} direction="right" />
                </th>
                <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white2)] font-normal text-left px-4 py-3.5 whitespace-nowrap">
                  Payable Date
                  <InfoIcon tip={COLUMN_TIPS.payable_date} direction="right" />
                </th>
                <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white2)] font-normal text-right px-4 py-3.5 whitespace-nowrap">
                  Distribution NAV
                  <InfoIcon tip={COLUMN_TIPS.nav} direction="left" />
                </th>
                <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white2)] font-normal text-right px-4 py-3.5 whitespace-nowrap">
                  LT Cap. Gain
                  <InfoIcon tip={COLUMN_TIPS.lt_cap_gain} direction="left" />
                </th>
                <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white2)] font-normal text-right px-4 py-3.5 whitespace-nowrap">
                  ST Cap. Gain
                  <InfoIcon tip={COLUMN_TIPS.st_cap_gain} direction="left" />
                </th>
                <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white2)] font-normal text-right px-4 py-3.5 whitespace-nowrap">
                  Dividend Income
                  <InfoIcon tip={COLUMN_TIPS.dividend_income} direction="left" />
                </th>
                <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white2)] font-normal text-right px-4 py-3.5 whitespace-nowrap">
                  Return of Capital
                  <InfoIcon tip={COLUMN_TIPS.return_of_capital} direction="left" />
                </th>
                <th className="font-[family-name:var(--font-instrument)] text-[11px] tracking-[0.12em] uppercase text-[var(--etf-white2)] font-normal text-right px-10 max-md:px-4 py-3.5 whitespace-nowrap">
                  Total Distribution
                  <InfoIcon tip={COLUMN_TIPS.total_distribution} direction="left" />
                </th>
              </tr>
            </thead>
            <tbody>
              {hasRows &&
                rows.map((row, i) => (
                  <tr
                    key={`${row.ex_div_date}-${i}`}
                    className="border-b border-[var(--etf-border2)] last:border-b-0 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white)] px-10 max-md:px-4 py-4">
                      {row.ex_div_date}
                    </td>
                    <td className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] px-4 py-4">
                      {row.record_date}
                    </td>
                    <td className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] px-4 py-4">
                      {row.payable_date}
                    </td>
                    <td className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] text-right px-4 py-4">
                      {row.nav}
                    </td>
                    <td className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] text-right px-4 py-4">
                      {row.lt_cap_gain}
                    </td>
                    <td className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] text-right px-4 py-4">
                      {row.st_cap_gain}
                    </td>
                    <td className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] text-right px-4 py-4">
                      {row.dividend_income}
                    </td>
                    <td className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white2)] text-right px-4 py-4">
                      {row.return_of_capital}
                    </td>
                    <td className="font-[family-name:var(--font-instrument)] text-[13px] text-[var(--etf-white)] font-medium text-right px-10 max-md:px-4 py-4">
                      {row.total_distribution}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {!hasRows && (
          <div className="mx-10 max-md:mx-4 mt-2 mb-7 max-md:mb-6 px-6 py-9 max-md:px-4 max-md:py-6 text-center border border-dashed border-[var(--etf-border)] rounded-[2px] bg-white/[0.015]">
            <p className="font-[family-name:var(--font-f37bolton)] text-[16px] text-[var(--etf-white)] tracking-[-0.01em] mb-2">
              No distributions have been declared yet
            </p>
            <p className="font-[family-name:var(--font-instrument)] italic text-[13px] text-[var(--etf-white3)] leading-[1.6] max-w-[580px] mx-auto m-0">
              {subject.charAt(0).toUpperCase() + subject.slice(1)} is expected to declare
              distributions{cadence ? ` on a ${cadence} basis` : ""}. Distribution history will
              populate this table after the Fund declares its first dividend.
            </p>
          </div>
        )}
      </div>

      <p className="font-[family-name:var(--font-instrument)] italic opacity-70 text-[11px] text-[var(--etf-white3)] leading-[1.6] px-10 max-md:px-4 pt-4 pb-7">
        Past distributions are not indicative of future distributions. Distributions are not
        guaranteed and may consist of ordinary income, qualified dividend income, capital gains, or
        a return of capital, as determined annually for federal income tax purposes.
      </p>
    </section>
  );
}
